import React, { useCallback, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'
import { motion } from 'framer-motion'

import { useDirection } from 'shared/hooks/useDirection'
import { useGetBrowserProperties } from 'shared/hooks/useGetBrowserProperties'
import useHideOverflow from 'shared/hooks/useHideOverflow'
import { useMediaMaxTablet } from 'shared/style/var'

import { logout } from 'features/auth/actions'
import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { DownloadAppButton } from 'components/DownloadAppButton/DownloadAppButton'
import { getHomeMenuItems, sideMenuOnlyItems } from 'components/Header/data'
import { MenuItem } from 'components/Header/MenuItem/MenuItem'
import SetLanguage from 'components/SetLanguage/SetLanguage'
import Button from 'components/UIKit/Button/Button'

import './SideMenu.scss'

interface ISideMenuProps {
  isOpen: boolean
  onToggleSideMenu: (val?: boolean) => void
}

const bgVariants = {
  open: {
    opacity: 1,
    '-webkit-backdrop-filter': 'blur(5px)',
    backdropFilter: 'blur(5px)',
    transition: { duration: 0.35 },
  },
  closed: {
    opacity: 0,
    transition: { duration: 0.35 },
    backdropFilter: 'blur(5px)',
    '-webkit-backdrop-filter': 'blur(5px)',
  },
}

const wrapperVariants = {
  open: {
    display: 'flex',
    transition: { duration: 0 },
  },
  closed: {
    display: 'none',
    transition: { when: 'afterChildren' },
  },
}

const b = block('side-menu')

export const SideMenu: React.FC<ISideMenuProps> = ({ isOpen, onToggleSideMenu }) => {
  const dispatch = useDispatch()
  const { isAndroid, isWebView } = useGetBrowserProperties()
  const isMobile = useMediaMaxTablet()

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)

  useHideOverflow(isOpen)

  const direction = useDirection()

  const showApkDownload = useMemo(() => isMobile && (isAndroid || isWebView), [isAndroid, isWebView, isMobile])

  const variants = useMemo(
    () => ({
      open: { opacity: 1, x: 0, transition: { duration: 0.35 } },
      closed: {
        opacity: 0,
        x: direction === 'ltr' ? '-100%' : '+100%',
        transition: { duration: 0.35 },
      },
    }),
    [direction],
  )

  const handleClose = useCallback(() => {
    onToggleSideMenu(false)
  }, [onToggleSideMenu])

  const handleLogoutClick = useCallback(() => {
    dispatch(logout())

    handleClose()
  }, [dispatch, handleClose])

  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)

  const handleMenuClose = useCallback(() => {
    setOpenMenuIndex(null)
  }, [])

  const handleMenuOpen = useCallback((index: number) => {
    setOpenMenuIndex(index)
  }, [])

  const itemsList = useMemo(() => {
    const allItems = [...getHomeMenuItems(isMobile), ...sideMenuOnlyItems]
    return allItems.map((item, index) => (
      <MenuItem
        key={item.textID}
        item={item}
        index={index}
        name={item.textID}
        subItems={item.subItems}
        isSideMenu
        callback={handleClose}
        isOpen={openMenuIndex === index}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
      />
    ))
  }, [handleMenuClose, handleMenuOpen, openMenuIndex])

  return (
    <motion.nav
      initial="closed"
      animate={isOpen ? 'open' : 'closed'}
      variants={wrapperVariants}
      className={b('wrapper')}>
      <motion.div className={b()} variants={variants}>
        {itemsList}
        <div className={b('divider')} />

        {showApkDownload && (
          <div className={b('download-wrapper')}>
            <DownloadAppButton />
          </div>
        )}

        <div className={b('language-selector')}>
          <SetLanguage />
        </div>
        {isAuth && (
          <div className={b('btn-wrapper')}>
            <Button text={locale.logout} callback={handleLogoutClick} colorType="secondary" />
          </div>
        )}
      </motion.div>
      <motion.div onClick={handleClose} className={b('close-block', { open: isOpen })} variants={bgVariants} />
    </motion.nav>
  )
}
